const STORAGE_PATH_RE = /\/(api\/)?storage\/(public-objects|objects)\//;

// One-shot cache-buster for storage URLs. Earlier server builds returned a
// generic, non-unique ETag (`"x-webp-w480"`) for every image at a given
// width+format. Browsers that cached those responses now have *poisoned*
// entries: any storage URL with the old ETag may render bytes from a
// different product. Bumping this constant appends `?_cb=<n>` to every
// generated storage URL, which the browser treats as a brand-new resource
// and refetches with the new (unique) ETag. Only bump when the cache
// contract changes; leave alone otherwise so long-lived `immutable`
// browser caches keep working.
const IMAGE_CACHE_BUST = 2;
function appendCacheBust(url: string): string {
  if (!STORAGE_PATH_RE.test(url) && !LEGACY_OBJECTS_RE.test(url)) return url;
  if (/[?&]_cb=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_cb=${IMAGE_CACHE_BUST}`;
}
// Legacy banner / category rows in the database sometimes carry a bare
// `/objects/uploads/...` path that predates the storageRouter mount. The
// API server only serves images under `/api/storage/objects/...`, so the
// legacy path returns the SPA's HTML fallback (13 KB, content-type
// text/html) instead of the actual WebP. We rewrite at the client layer
// so legacy URLs are recognized as storage URLs AND benefit from
// `?w=N` responsive variants.
const LEGACY_OBJECTS_RE = /^\/objects\//;
function normalizeStorageUrl(url: string): string {
  return LEGACY_OBJECTS_RE.test(url) ? `/api/storage${url}` : url;
}

/**
 * Percent-encode unsafe characters in an image URL's path and query while
 * preserving the protocol, host, and any already-encoded sequences.
 *
 * Many product images point to external hosts whose paths contain raw spaces
 * or non-ASCII characters (e.g. Arabic letters, the tatweel U+0640). Some
 * upstream servers reject such requests, so we normalize the URL before
 * handing it to the browser.
 *
 * Idempotent: re-encoding an already-encoded URL is a no-op.
 */
export function safeImageUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  // Whitespace-only strings are treated as empty so callers don't render
  // <img src=""> (which causes the browser to refetch the current page).
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  url = trimmed;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (LEGACY_OBJECTS_RE.test(url)) url = `/api/storage${url}`;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const u = new URL(url, base);
    const encodedPath = u.pathname
      .split("/")
      .map((seg) => {
        if (!seg) return seg;
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
    u.pathname = encodedPath;
    if (url.startsWith("/") && !url.startsWith("//")) {
      return appendCacheBust(`${u.pathname}${u.search}${u.hash}`);
    }
    return appendCacheBust(u.toString());
  } catch {
    return appendCacheBust(url);
  }
}

export function isStorageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  if (typeof window !== "undefined") {
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return false;
      return STORAGE_PATH_RE.test(u.pathname) || LEGACY_OBJECTS_RE.test(u.pathname);
    } catch {
      return false;
    }
  }
  return STORAGE_PATH_RE.test(url) || LEGACY_OBJECTS_RE.test(url);
}

function withWidth(url: string, width: number): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}w=${width}`;
}

const DEFAULT_WIDTHS = [320, 480, 640, 800, 1080, 1280, 1600, 1920];

export function buildSrcSet(url: string | null | undefined, widths: number[] = DEFAULT_WIDTHS): string | undefined {
  if (!isStorageUrl(url)) return undefined;
  const normalized = appendCacheBust(normalizeStorageUrl(url!));
  return widths.map((w) => `${withWidth(normalized, w)} ${w}w`).join(", ");
}

// ─── Per-tenant WebP conversion config ────────────────────────────────────────
// Set by ThemeProvider once theme settings are loaded from /api/theme/settings.
// Default: enabled=true, quality=85 — mirrors img-proxy defaults so no extra
// URL param is added until the merchant explicitly changes quality from 85.
let _webpCfg: { enabled: boolean; quality: number } = { enabled: true, quality: 85 };

/**
 * Called by ThemeProvider after loading theme settings. Updates the module-level
 * WebP config so all subsequent img-proxy URL builds carry the right `q` param
 * or `nowebp=1` flag.
 */
export function setWebpConfig(cfg: { enabled: boolean; quality: number }): void {
  _webpCfg = { enabled: cfg.enabled, quality: Math.max(50, Math.min(100, cfg.quality)) };
}

/**
 * Route an absolute external image URL (luxuryav.net, *.r2.dev, etc.) through
 * the API's `/api/img-proxy.webp` so it can be resized + reformatted server-side.
 * Returns `undefined` for storage URLs (use `buildSrcSet` directly), relative
 * URLs, or non-HTTP schemes.
 *
 * Uses the `/api/img-proxy.webp` path (not `/api/img-proxy?fmt=webp`) so that
 * Cloudflare recognises the `.webp` file extension and edge-caches the response
 * on all plans — including custom merchant domains where we have no CF zone
 * access. This is the key to low LCP: the hero banner image is served from a
 * CF edge PoP (~30ms) instead of the Oracle origin (~300-800ms) on all but the
 * first cold miss. The lcp-hint endpoint uses the same format so the preloaded
 * URL exactly matches the rendered <img> srcset URL.
 *
 * Appends `&q=N` when WebP quality differs from the img-proxy default (85), or
 * `&nowebp=1` when the merchant has disabled WebP conversion.
 */
function proxiedExternalUrl(url: string, width?: number): string | undefined {
  if (!/^https?:\/\//i.test(url)) return undefined;
  const w = width ? `&w=${width}` : "";
  if (!_webpCfg.enabled) {
    // Merchant disabled WebP: use original /img-proxy route with nowebp flag.
    // These responses carry Vary:Accept and aren't CF-cached, but that's
    // acceptable for the edge case where a merchant opts out of WebP.
    return `/api/img-proxy?url=${encodeURIComponent(url)}${w}&nowebp=1`;
  }
  // Only append q when it differs from the img-proxy default (85) to keep URLs
  // stable for merchants who haven't changed the quality slider.
  const wq = _webpCfg.quality !== 85 ? `&q=${_webpCfg.quality}` : "";
  // _v=1: cache-busting version — increment when a deployment changes the
  // image format/encoding so Cloudflare edge cache invalidates immediately.
  // lcp-hint.ts uses the same _v value so the preloaded URL exactly matches
  // the rendered <img> srcset URL (mismatch = preload ignored = LCP regression).
  return `/api/img-proxy.webp?url=${encodeURIComponent(url)}${w}${wq}&_v=1`;
}

export function buildExternalSrcSet(
  url: string | null | undefined,
  widths: number[] = [160, 320, 640],
): string | undefined {
  if (!url) return undefined;
  if (isStorageUrl(url)) return undefined;
  if (url.startsWith("data:") || url.startsWith("blob:")) return undefined;
  if (!/^https?:\/\//i.test(url)) return undefined;
  return widths
    .map((w) => `${proxiedExternalUrl(url, w)} ${w}w`)
    .join(", ");
}

/**
 * Resolve a final `<img src>` for any URL: storage URLs are returned as-is
 * (with cache-bust + width), external HTTP(S) URLs are routed through the
 * image proxy so they can be resized + reformatted.
 */
export function resolveImageSrc(
  url: string | null | undefined,
  width?: number,
): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (isStorageUrl(url)) {
    return width
      ? withWidthIfStorage(url, width) ?? undefined
      : safeImageUrl(url);
  }
  if (/^https?:\/\//i.test(url)) return proxiedExternalUrl(url, width);
  return safeImageUrl(url);
}

/**
 * Convenience that returns a srcset for any input — storage OR external —
 * by delegating to `buildSrcSet` first and falling back to the external
 * proxy variant. Useful when a single `<img>` may be either.
 */
export function buildAnySrcSet(
  url: string | null | undefined,
  storageWidths: number[] = DEFAULT_WIDTHS,
  externalWidths: number[] = [160, 320, 640],
): string | undefined {
  return buildSrcSet(url, storageWidths) ?? buildExternalSrcSet(url, externalWidths);
}

export function withWidthIfStorage(url: string | null | undefined, width: number): string | null | undefined {
  if (!url) return url;
  if (!isStorageUrl(url)) return url;
  return appendCacheBust(withWidth(normalizeStorageUrl(url), width));
}

/**
 * Rewrite legacy `/objects/...` storage paths to the canonical
 * `/api/storage/objects/...` path. Pass-through for anything else.
 * Use this before assigning to `<img src>` so legacy DB rows still load.
 */
export function canonicalizeImageUrl(url: string | null | undefined): string | null | undefined {
  if (!url) return url;
  const rewritten = LEGACY_OBJECTS_RE.test(url) ? `/api/storage${url}` : url;
  return appendCacheBust(rewritten);
}

/**
 * Append a version stamp to an image URL so that browsers re-fetch it
 * whenever the underlying record's `updatedAt` changes. This works around
 * long-lived `immutable` cache headers on storage URLs without sacrificing
 * cache performance for unchanged images.
 */
export function withVersion(
  url: string | null | undefined,
  version: string | number | Date | null | undefined,
): string | undefined {
  if (!url) return undefined;
  if (version == null) return url;
  let stamp: number;
  if (version instanceof Date) stamp = version.getTime();
  else if (typeof version === "number") stamp = version;
  else {
    const t = Date.parse(String(version));
    stamp = Number.isFinite(t) ? t : 0;
  }
  if (!stamp) return url;
  const normalized = appendCacheBust(LEGACY_OBJECTS_RE.test(url) ? `/api/storage${url}` : url);
  const sep = normalized.includes("?") ? "&" : "?";
  return `${normalized}${sep}v=${stamp}`;
}

export const PRODUCT_SIZES = "(max-width:640px) 50vw, (max-width:1024px) 33vw, 25vw";
export const HERO_SIZES = "100vw";
export const CATEGORY_SIZES = "(max-width:640px) 50vw, 25vw";
